import { Button, Card, CardContent, Checkbox, CircularProgress, Icon, Tooltip, Typography } from '@material-ui/core';
import makeStyles from '@material-ui/core/styles/makeStyles';
import Alert from 'components/Alert';
import PermissionHide from 'components/PermissionHide';
import { FINANCIAL_BLOCK_TOOLTIP_MESSAGE } from 'helpers/constants';
import { userHasNoDocumentAlert } from 'helpers/functions';
import React, { Fragment, useCallback, useMemo, useState } from 'react';
import { FormattedNumber } from 'react-intl';
import { toast } from 'react-toastify';
import { useCallbackObservable, useObservable } from 'react-use-observable';
import { tap } from 'rxjs/operators';
import ConfirmTransactionMessage from 'v2/components/Shared/ConfirmTransactionMessage';
import Tour from 'v2/components/Shared/Tour';
import { EFromPageTransfer } from 'v2/interfaces/financial';
import { EAnticipationStatus } from 'v2/interfaces/user';
import eduzzAccountsService from 'v2/services/eduzzAccount';
import financialService from 'v2/services/financial';

import type IProfile from 'v2/interfaces/profile';

import { steps } from './tour';
import { FidcAlertModal } from '../FidcAlertModal';
import ModalFiscal from '../ModalFiscal';

import type User from 'classes/User';

const useStyle = makeStyles((theme) => ({
  root: {
    height: '100%',
    borderRadius: 4,
  },
  content: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
  },
  container: {
    display: 'flex',
    justifyContent: 'space-between',
  },
  title: {
    fontSize: 16.5,
    color: '#586375',
  },
  subTitle: {
    fontSize: 11,
    color: '#586375',
  },
  valueContainer: {
    display: 'flex',
    justifyContent: 'flex-end',
    alignItems: 'center',
  },
  buttonContainer: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  transferButton: {
    background: '#009358',
    color: 'white',
    float: 'right',
    [theme.breakpoints.down('sm')]: {
      float: 'none',
    },
    '&:hover': {
      background: '#007A49',
    },
    '&:disabled': {
      background: '#eee',
    },
  },
}));

interface IProps {
  permissionArea: string;
  user: User;
}

const CardTransferPJ = ({ permissionArea, user }: IProps) => {
  const classes = useStyle({});

  const [loading, setLoading] = useState(false);
  const [visibleFiscal, setVisibleFiscal] = useState(false);
  const [profile, setProfile] = useState<IProfile>(null);
  const [transferChecked, setTransferChecked] = useState(false);
  const [anticipationChecked, setAnticipationChecked] = useState(false);
  const [userState, setUserState] = useState({ ...user });
  const [showFidcAlertModal, setShowFidcAlertModal] = useState(false);
  const cannotAntecipate = eduzzAccountsService.verifyUserHasBetaModule('cannot-antecipate');

  const minToTransfer = user?.international_account ? 5000 : 100;

  const [data, error] = useObservable(() => financialService.listAnticipation().pipe(), []);
  const [bankAccounts, bankAccountsError] = useObservable(() => financialService.listBankAccounts({ active: 'true' }).pipe(), []);

  const [betaModules] = useObservable(() => eduzzAccountsService.getBetaModules(), []);
  const userHasBlockTransferBetaModule = betaModules?.includes('block_transfer');

  const handleCheckTransfer = useCallback(() => setTransferChecked(!transferChecked), [transferChecked]);
  const handleCheckAnticipation = useCallback(() => setAnticipationChecked(!anticipationChecked), [anticipationChecked]);
  const handleCloseModalFiscal = useCallback(() => {
    setVisibleFiscal(false);
  }, []);

  const totalToTransfer = useMemo(() => {
    const anticipationValue = anticipationChecked ? data?.availableAnticipation || 0 : 0;
    const transferValue = transferChecked ? data?.availableTransfer || 0 : 0;

    return anticipationValue + transferValue;
  }, [anticipationChecked, data, transferChecked]);

  const disableTransferButton = useMemo(() => {
    if (!transferChecked && anticipationChecked) {
      return true;
    }

    if (totalToTransfer < minToTransfer) {
      return true;
    }

    if (userHasBlockTransferBetaModule) {
      return true;
    }

    return false;
  }, [anticipationChecked, minToTransfer, totalToTransfer, transferChecked, userHasBlockTransferBetaModule]);

  const code = useMemo(() => (userState?.crossborder ? 'USD' : 'BRL'), [userState]);
  const invoices = useMemo(() => (anticipationChecked && data?.invoices) || [], [anticipationChecked, data]);

  const bankSelected = useMemo(() => {
    if (bankAccounts?.length) {
      const findAccount = bankAccounts.find((item) => item.main);

      if (!findAccount) {
        return bankAccounts[0];
      }

      return findAccount;
    }

    return null;
  }, [bankAccounts]);

  const isLoading = data === null && error === undefined;
  const isLoadingBankAccounts = bankAccounts === null && bankAccountsError === undefined;

  const [sendTransfer] = useCallbackObservable(() => {
    setLoading(true);
    return financialService
      .sendTransfer({ invoices, transfer_method_id: bankSelected.id, value: totalToTransfer, from: EFromPageTransfer.NEW_CARD })
      .pipe(
        tap(
          () => {
            toast.success('Sucesso, transferência enviada para processamento com êxito!');
            setLoading(false);
            setUserState({ ...user, has_transfer: true });
          },
          (err) => {
            setLoading(false);
            toast.error(err);
          }
        )
      );
  }, [totalToTransfer, bankSelected, invoices]);

  const makeTransfer = useCallback(async () => {
    setLoading(true);

    const { profile, hasDocument } = await userHasNoDocumentAlert();

    if (!hasDocument) {
      setLoading(false);
      setProfile(profile);
      setVisibleFiscal(true);
      return;
    }

    setLoading(false);

    const confirm = await Alert.confirm({
      message: <ConfirmTransactionMessage value={totalToTransfer} bankAccount={bankSelected} />,
      title: 'Atenção',
      buttonOk: 'Confirmar',
      confirmation: true,
    });

    if (confirm) {
      sendTransfer();
    }
  }, [bankSelected, sendTransfer, totalToTransfer]);

  const handleSubmit = useCallback(async () => {
    if (!transferChecked && !anticipationChecked) {
      toast.error('Selecione os valores para transfêrencia');
      return;
    }

    const showGalaxyEligibleAlert = user?.eligible;
    if (showGalaxyEligibleAlert) {
      setShowFidcAlertModal(true);
      return;
    }

    makeTransfer();
  }, [transferChecked, anticipationChecked, user?.eligible, makeTransfer]);

  const handleCloseFidcAlertModal = () => {
    setShowFidcAlertModal(false);
    makeTransfer();
  };

  const renderTooltipValue = useMemo(() => {
    if (userHasBlockTransferBetaModule) {
      return FINANCIAL_BLOCK_TOOLTIP_MESSAGE;
    }

    if (!transferChecked && anticipationChecked) {
      return 'Para antecipar é necessário selecionar o saldo a transferir';
    }

    if (totalToTransfer < minToTransfer) {
      return (
        <p>
          Valor mínimo para transferir: <FormattedNumber value={minToTransfer} format={code}></FormattedNumber>
        </p>
      );
    }

    return '';
  }, [anticipationChecked, code, minToTransfer, totalToTransfer, transferChecked, userHasBlockTransferBetaModule]);

  const disableCheckbox = useMemo(() => {
    if (isLoadingBankAccounts) {
      return true;
    }

    if (!bankSelected) {
      return true;
    }

    if (userState?.has_transfer) {
      return true;
    }

    if (bankAccountsError) {
      return true;
    }

    return false;
  }, [bankAccountsError, bankSelected, isLoadingBankAccounts, userState]);

  const renderTransferButton = useMemo(() => {
    if (isLoadingBankAccounts) return <div />;

    if (!bankSelected) {
      return <div>Você precisa de uma conta bancária cadastrada para transferir</div>;
    }

    if (userState?.has_transfer) {
      return <div>Você tem transferências em processamento</div>;
    }

    if (bankAccountsError) {
      return <div>Erro ao buscar contas bancárias</div>;
    }

    return (
      <div className={classes.buttonContainer}>
        {cannotAntecipate ? (
          <p></p>
        ) : (
          <a
            href='https://ajuda.eduzz.com/hc/pt-br/articles/4402057357211-Como-funciona-a-antecipa%C3%A7%C3%A3o-%C3%93rbita-'
            target='_blank'
            rel='noopener noreferrer'
          >
            Entenda a antecipação
          </a>
        )}
        <Tooltip title={renderTooltipValue} placement='top'>
          <div>
            <Button className={classes.transferButton} disabled={loading || disableTransferButton} onClick={handleSubmit} id='myTransferButton'>
              <Icon style={{ marginRight: '0.5rem', fontSize: 14 }}>attach_money</Icon>Transferir agora
            </Button>
          </div>
        </Tooltip>
      </div>
    );
  }, [
    bankAccountsError,
    bankSelected,
    cannotAntecipate,
    classes.buttonContainer,
    classes.transferButton,
    disableTransferButton,
    handleSubmit,
    isLoadingBankAccounts,
    loading,
    renderTooltipValue,
    userState?.has_transfer,
  ]);

  const isAnticipationBlocked = user?.anticipation_status === EAnticipationStatus.BLOQUEADO;

  return (
    <Fragment>
      <ModalFiscal opened={visibleFiscal} onClose={handleCloseModalFiscal} onComplete={handleCloseModalFiscal} profile={profile} />

      <Tour
        name='card_transfer_pj_tour'
        steps={steps}
        floaterProps={{
          disableAnimation: true,
        }}
      />

      <div id='transfer-card-pj' className={classes.root}>
        <Card elevation={1} square className={classes.root}>
          <CardContent className={classes.content}>
            <div className={classes.container}>
              <div>
                <Typography color='inherit' component='h2' variant='h6'>
                  Carteira
                </Typography>
                <Typography color='inherit' gutterBottom variant='subtitle1' component='span' style={{ margin: 0, fontSize: 12 }}>
                  Pessoa Jurídica
                </Typography>
              </div>

              <div id='transfer-card-pj-values'>
                <div>
                  <Typography align='right' component='h2' className={classes.title}>
                    Disponível para Transferência:
                  </Typography>

                  <Typography align='right' className={classes.subTitle}>
                    Valor total liberado para ser transferido
                  </Typography>
                </div>

                <div className={classes.valueContainer}>
                  <Typography align='right' component='h2' variant='h5'>
                    {!isLoading ? <FormattedNumber format={code} value={data?.availableTransfer || 0} /> : <CircularProgress size={20} />}
                  </Typography>

                  {!isLoading && <Checkbox disabled={disableCheckbox} checked={transferChecked} onChange={handleCheckTransfer} />}
                </div>

                {!cannotAntecipate && (
                  <>
                    <div>
                      <Typography align='right' component='h2' className={classes.title}>
                        Disponível para Antecipar:
                      </Typography>

                      <Typography align='right' className={classes.subTitle}>
                        Valores do saldo futuro que podem ser antecipados
                      </Typography>
                    </div>
                    <div className={classes.valueContainer}>
                      <Typography align='right' component='h2' variant='h5'>
                        {!isLoading ? <FormattedNumber format={code} value={data?.availableAnticipation || 0} /> : <CircularProgress size={20} />}
                      </Typography>

                      {!isLoading && (
                        <Tooltip
                          title='Antecipação bloqueada por alto índice de chargeback'
                          placement='top'
                          arrow
                          disableFocusListener={!isAnticipationBlocked}
                          disableHoverListener={!isAnticipationBlocked}
                          disableTouchListener={!isAnticipationBlocked}
                        >
                          <div>
                            <Checkbox
                              disabled={disableCheckbox || isAnticipationBlocked}
                              checked={anticipationChecked}
                              onChange={handleCheckAnticipation}
                            />
                          </div>
                        </Tooltip>
                      )}
                    </div>
                  </>
                )}
              </div>
            </div>

            <PermissionHide action={`myeduzz:${permissionArea}:solicitar-transferencia`}>{renderTransferButton}</PermissionHide>
          </CardContent>
        </Card>
      </div>
      <FidcAlertModal isOpen={showFidcAlertModal} onClose={handleCloseFidcAlertModal} />
    </Fragment>
  );
};

export default CardTransferPJ;
